import React from 'react';
import classnames from 'classnames/bind';
import { useDocumentTitle } from '../../shared/use-document-title';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { LastCheckTime } from '../../components/LastCheckTime';
import { Loader } from '../../components/Loader';
import { SectionWithSubscriptionBadge } from '../../components/SectionWithSubscriptionBadge';
import { useSearchEmailForm } from '../../shared/use-search-email-form';
import { useSearchForm } from '../../shared/use-search-form';
import { useRouting } from '../../shared/use-routing';
import { useUser } from '../../shared/use-user';
import { useTrackedEmails } from '../../shared/use-tracked-emails';
import { dynamicPagePathnames, pagePathnames } from '../page-routes';
import linkImageSrc from '../../assets/images/feature-link.png';
import syllabusImageSrc from '../../assets/images/feature-syllabus.png';
import styles from './MainPage.css';

const cx = classnames.bind(styles);

const FEATURES = [
    {
        id: 2,
        title: 'Safe external links',
        description: 'Identifies links that open in a new tab and ensures they are secure. Prevents hidden security risks by blocking potential unauthorized access from the new site to the original page.'
    },
    {
        id: 3,
        title: 'Force secure connection',
        description: 'If your connection to the website is not secure, we will warn you not to enter personal data or banking card information.'
    },
    {
        id: 4,
        title: 'Expand short links',
        description: 'Shows the full destination of shortened links before you open them.'
    },
    {
        id: 5,
        title: 'Block dangerous sites',
        description: 'Prevents access to harmful websites to keep you safe.'
    },
    {
        id: 6,
        title: 'Mark unsafe links',
        description: 'Highlights risky links on the page before you click.'
    },
    {
        id: 7,
        title: 'Check browsing history',
        description: 'Scans your history for visits to dangerous sites.'
    },
    {
        id: 8,
        title: 'Warn about suspicious downloads',
        description: 'Alerts you if a downloaded file might be unsafe.'
    }
];

function MainPage() {
    useDocumentTitle('Main');

    const {fields, changeField} = useSearchEmailForm();
    const {fields: reputationFields, changeField: changeReputationField} = useSearchForm();
    const {navigate} = useRouting();
    const {userData} = useUser();
    const {trackedEmailList, isLoading} = useTrackedEmails();

    const submitForm = (value, name, url) => e => {
        e.preventDefault();

        navigate(`${url}${value ? `?${name}=${value}` : ''}`);
    };

    const getTrackedEmailButton = ({totalBreaches, resolvedBreaches}) => {
        if (totalBreaches === resolvedBreaches) {
            return 'No action needed';
        }

        if (totalBreaches - resolvedBreaches === 1) {
            return 'Resolve 1 breach';
        }

        return 'Resolve breaches';
    };

    return (
        <div className={styles.root}>
            <section className={styles.section}>
                <h1 className={styles.title}>Data Breach Analyzer</h1>
                <p className={styles.description}>
                    <strong>Data Breach Analyzer</strong> checks if your information appears in known leaks. It scans our database of reported breaches and notifies you if your email or other data is exposed.
                </p>
                <ul className={styles.list}>
                    <li className={styles.listItem}>
                        <span>Known Compromised Websites</span>
                        <span>890</span>
                    </li>
                    <li className={styles.listItem}>
                        <span>Known Compromised Accounts</span>
                        <span>14,661,189,352</span>
                    </li>
                </ul>
            </section>
            <section className={cx('secondarySection', 'withIndent')}>
                <h2 className={styles.title}>Email Checker</h2>
                <p className={styles.description}>
                    Quickly scan known breaches and see where your email has been exposed.
                </p>
                <form
                    className={styles.form}
                    onSubmit={submitForm(fields.email, 'email', pagePathnames.emailCheck)}
                >
                    <Input
                        name='email'
                        placeholder='Enter email'
                        onChange={changeField}
                        value={fields.email}
                        className={styles.input}
                    />
                    <Button
                        className={styles.button}
                        link={{
                            to: `${pagePathnames.emailCheck}${fields.email ? `?email=${fields.email}` : ''}`
                        }}
                        inputProps={{autoComplete: 'email', type: 'email'}}
                        label='check it'
                    />
                </form>
            </section>
            <SectionWithSubscriptionBadge
                isSubscription={userData.isActiveSubscription}
                textBefore='Unlock with a'
                mainText='Subscription'
            >
                  <section className={cx('secondarySection', 'withIndentChildren')}>
                    <h2 className={styles.title}>Email Breach Monitor</h2>
                    <p className={styles.description}>
                        Continuously scans for breaches tied to your email and keeps you informed.
                    </p>
                    {isLoading
                        ? <Loader variant='secondary' className={styles.loader}/>
                        : (
                            <ul className={styles.trackedEmailList}>
                                {trackedEmailList.length === 0 && (
                                    <li className={styles.trackedEmailItem}>
                                        <div className={styles.trackedEmailItemDetails}>
                                            <div className={styles.trackedEmailItemInformation}>
                                                <Icon name='lock' />
                                                <span>{userData.email}</span>
                                            </div>
                                        </div>
                                        <div className={styles.divider} />
                                        <div className={styles.breachDetails}>
                                            <div className={styles.breachDetailsItem}>
                                                <span>Breaches:</span>
                                                <span>-</span>
                                            </div>
                                            <div className={styles.breachDetailsItem}>
                                                <span>Issues resolved:</span>
                                                <span>-</span>
                                            </div>
                                        </div>
                                        <div className={styles.divider} />
                                        <Button
                                            label='No action needed'
                                            disabled
                                        />
                                    </li>
                                )}
                                {trackedEmailList.map(trackedEmailItem => (
                                    <li
                                        key={trackedEmailItem.id}
                                        className={styles.trackedEmailItem}
                                    >
                                        <div className={styles.trackedEmailItemDetails}>
                                            <div className={styles.trackedEmailItemInformation}>
                                                <Icon name='lock' />
                                                <span>{trackedEmailItem.email}</span>
                                            </div>
                                            <LastCheckTime label={trackedEmailItem.lastCheckedTimeAgo} />
                                        </div>
                                        <div className={styles.divider} />
                                        <div className={styles.breachDetails}>
                                            <div className={styles.breachDetailsItem}>
                                                <span>Breaches:</span>
                                                <span>{trackedEmailItem.totalBreaches}</span>
                                            </div>
                                            <div className={styles.breachDetailsItem}>
                                                <span>Issues resolved:</span>
                                                <span>{trackedEmailItem.resolvedBreaches}</span>
                                            </div>
                                        </div>
                                        <div className={styles.divider} />
                                        <Button
                                            label={getTrackedEmailButton(trackedEmailItem)}
                                            disabled={!userData.isActiveSubscription}
                                            link={{
                                                to: userData.isActiveSubscription ? dynamicPagePathnames.getBreachMonitor(trackedEmailItem.id) : '/'
                                            }}
                                        />
                                    </li>
                                ))}
                            </ul>
                                            
                        )
                    }
                </section>
            </SectionWithSubscriptionBadge>
            <SectionWithSubscriptionBadge
                isSubscription={userData.isActiveSubscription}
                textBefore='To start using'
                mainText='Subscribe'
            >
                <section className={cx('secondarySection', 'withIndent')}>
                    <h2 className={styles.title}>Link Reputation Checker</h2>
                    <p className={styles.description}>
                        Quickly find out if a website or email poses a threat — one check for peace of mind.
                    </p>
                    <form
                        className={styles.form}
                        onSubmit={submitForm(reputationFields.searchText, 'searchText', pagePathnames.reputationCheck)}
                    >
                        <Input
                            name='searchText'
                            placeholder='Enter a link, website, or email'
                            onChange={changeReputationField}
                            value={reputationFields.searchText}
                            className={styles.input}
                            disabled={!userData.isActiveSubscription}
                        />
                        <Button
                            className={styles.button}
                            link={{
                                to: `${pagePathnames.reputationCheck}${reputationFields.searchText ? `?searchText=${reputationFields.searchText}` : ''}`
                            }}
                            disabled={!userData.isActiveSubscription}
                            label='check it'
                        />
                    </form>
                </section>
            </SectionWithSubscriptionBadge>
            <h2 className={styles.secondaryTitle}>Upcoming service updates</h2>
            <p className={styles.description}>
                Features we're actively developing.
            </p>
            <section className={styles.tertiarySection}>
                <img
                    alt='link'
                    src={linkImageSrc}
                    className={styles.smallImage}
                />
                <h3 className={styles.tertiaryTitle} style={{marginBottom: 24}}>Link security features</h3>
                <ul className={styles.featureList}>
                    {FEATURES.map(feature => (
                        <li key={feature.id}>
                            <p>{feature.title}</p>
                            <p>{feature.description}</p>
                        </li>
                    ))}
                </ul>
            </section>
            <section className={styles.tertiarySection}>
                <img
                    alt='syllabus'
                    src={syllabusImageSrc}
                    className={styles.smallImage}
                />
                <h3 className={styles.tertiaryTitle}>
                    Extended curriculum
                </h3>
                <p className={styles.description} style={{marginBottom: 0}}>
                    An extended curriculum include advanced subject studies, elective courses, interdisciplinary approaches, practical projects, training and personalized learning paths.
                </p>
            </section>
        </div>
    )
};

export {
    MainPage
};
